import { useEffect, useRef, type ReactNode } from 'react';
import { dehydrate, hydrate, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  clearAllSnapshots,
  currentBuildId,
  isPersistedRoot,
  persistKey,
  readSnapshot,
  serializeSnapshot,
  shouldPersistQuery,
  writeSnapshot,
} from '@/lib/query-cache-persist';

/** Coalesce bursts of cache updates (a dashboard mount is ~20 in a second). */
const WRITE_THROTTLE_MS = 1000;

function storage(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}

/**
 * Restores the persisted dashboard slice of the query cache for the signed-in
 * email user, and keeps it written back. See src/lib/query-cache-persist.ts
 * for what is persisted and why it is safe.
 *
 * Hydration happens DURING RENDER, before `children` render, on purpose: the
 * dashboard's queries are all gated on the profile, so the render in which the
 * profile first appears is the same render in which they first read the cache.
 * Hydrating in an effect would run after that read and the first paint would
 * still be a skeleton. `hydrate` never overwrites newer data already in the
 * cache, and the ref guard makes it once per scope, so this is idempotent.
 */
export function QueryCachePersistence({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { profile, loginMethod, activeCompanyId } = useAuth();

  // Kiosk / PIN sessions are shared devices: never persisted.
  const key =
    loginMethod === 'email'
      ? persistKey({ profileId: profile?.id, companyId: activeCompanyId ?? profile?.company_id })
      : null;

  const hydratedKey = useRef<string | null>(null);
  if (key && hydratedKey.current !== key) {
    hydratedKey.current = key;
    const snapshot = readSnapshot(storage(), key, currentBuildId(), Date.now());
    if (snapshot) {
      try {
        hydrate(queryClient, snapshot);
      } catch {
        // A malformed snapshot must never take the app down with it.
      }
    }
  }

  // Remember whether this tab ever had a persisted scope, so signing out can
  // be told apart from "profile not loaded yet".
  const hadKey = useRef(false);

  useEffect(() => {
    if (!key) {
      if (hadKey.current) {
        // Signed out (or dropped to a PIN session). signOut already clears
        // localStorage for email users; this covers the paths that don't,
        // and runs after any pending write below has been cancelled.
        clearAllSnapshots(storage());
        hadKey.current = false;
      }
      hydratedKey.current = null;
      return;
    }
    hadKey.current = true;

    const buster = currentBuildId();
    let timer: ReturnType<typeof setTimeout> | null = null;

    const flush = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      const state = dehydrate(queryClient, { shouldDehydrateQuery: shouldPersistQuery });
      writeSnapshot(storage(), key, serializeSnapshot(state, buster, Date.now()));
    };

    const schedule = () => {
      if (timer) return;
      timer = setTimeout(flush, WRITE_THROTTLE_MS);
    };

    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (!isPersistedRoot(event.query.queryKey[0])) return;
      // Only fresh data or a removal changes what belongs on disk. Fetch-status
      // churn, observer bookkeeping and hydrate's own `setState` do not.
      if (event.type === 'removed' || (event.type === 'updated' && event.action.type === 'success')) {
        schedule();
      }
    });

    // A backgrounded phone may be killed without another chance to write.
    const onHide = () => {
      if (document.visibilityState === 'hidden' && timer) flush();
    };
    document.addEventListener('visibilitychange', onHide);

    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', onHide);
      // Cancel rather than flush: the scope is changing (sign-out, company
      // switch), and writing now would save this scope's cache mid-teardown.
      if (timer) clearTimeout(timer);
    };
  }, [key, queryClient]);

  return <>{children}</>;
}
